import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { EmitterService } from '../../shared/services/emitter.service';
import { OverlayService } from '../../shared/services/overlayService';
import { SubsectionService } from '../../shared/services/subsection.service';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  ShareLink,
  ShareLinkAsset,
  ShareLinkAssetEntityTypes,
  ShareLinkSimple,
  SubsectionNavigationParams,
} from '../../shared/interfaces';
import { ShareLinkTreeService } from '../services/share-link-tree.service';
import { getVersionId } from './subsection-utilities';
import { SHARE_LINK_EVENT, ShareLinkAssetVM } from '../models';

export function prepareShareLinkAssetsData(shareLink: ShareLink): void {
  if (!shareLink.assets) {
    throw new Error('Assets are required');
  }
  shareLink.assets.forEach((asset) => {
    prepareShareLinkAsset(asset);
  });
}

export function prepareShareLinkAsset(asset: ShareLinkAsset): void {
  switch (asset.entityType) {
    case ShareLinkAssetEntityTypes.FOLDER:
      asset.itemId = asset.topicId!;
      break;
    case ShareLinkAssetEntityTypes.SUBFOLDER:
      asset.itemId = asset.optionId!;
      break;
    case ShareLinkAssetEntityTypes.VERSION:
      asset.itemId = `${asset.optionId}-${asset.version}`;
      break;
    case ShareLinkAssetEntityTypes.FILE:
      asset.itemId = asset.fileId!;
      if (asset.previewImages && asset.previewImages.length > 0) {
        asset.signedURL = asset.previewImages[0] ?? '';
        asset.contentType = asset.contentTypes?.[0] ?? '';
      }
      break;
    case ShareLinkAssetEntityTypes.SUBSECTION:
      asset.itemId = asset.subSectionId!;
      break;
  }
}

function getSubsectionId(temporaryAsset: ShareLinkAssetVM): string {
  if (temporaryAsset.subsection?.id) {
    return temporaryAsset.subsection.id;
  }
  if (temporaryAsset.topic?.subsection) {
    return temporaryAsset.topic.subsection;
  }
  if (temporaryAsset.option?.subsection) {
    return temporaryAsset.option.subsection;
  }
  return '';
}

function getTopicId(temporaryAsset: ShareLinkAssetVM): string {
  return temporaryAsset.topic?.topicId || temporaryAsset.option?.topicId || '';
}

function createBaseAsset(temporaryAsset: ShareLinkAssetVM): Partial<ShareLinkAsset> {
  return {
    subSectionId: getSubsectionId(temporaryAsset),
    topicId: getTopicId(temporaryAsset),
    optionId: temporaryAsset.option?.optionId ?? '',
    version: temporaryAsset.version?.versionNumber ?? 0,
    fileId: '',
    entityType: temporaryAsset.type,
  };
}

function createSubsectionAsset(temporaryAsset: ShareLinkAssetVM, baseAsset: Partial<ShareLinkAsset>): ShareLinkAsset {
  if (!temporaryAsset.subsection) {
    throw new Error('Subsection data is required for SUBSECTION type');
  }
  const subsectionId: string = temporaryAsset.subsection.id ?? '';
  return {
    ...baseAsset,
    itemId: subsectionId,
    name: temporaryAsset.subsection.name ?? '',
  } as ShareLinkAsset;
}

function createFolderAsset(temporaryAsset: ShareLinkAssetVM, baseAsset: Partial<ShareLinkAsset>): ShareLinkAsset {
  if (!temporaryAsset.topic) {
    throw new Error('Topic data is required for FOLDER type');
  }
  return {
    ...baseAsset,
    itemId: temporaryAsset.topic.topicId,
    name: temporaryAsset.topic.title,
  } as ShareLinkAsset;
}

function createSubfolderAsset(temporaryAsset: ShareLinkAssetVM, baseAsset: Partial<ShareLinkAsset>): ShareLinkAsset {
  if (!temporaryAsset.option) {
    throw new Error('Option data is required for SUBFOLDER type');
  }
  const asset: ShareLinkAsset = {
    ...baseAsset,
    itemId: temporaryAsset.option.optionId,
    name: temporaryAsset.option.title,
  } as ShareLinkAsset;

  if (temporaryAsset.option.simpleVersions?.length === 1) {
    asset.singleVersionNumber = temporaryAsset.option.simpleVersions[0].versionNumber;
    asset.versionId = getVersionId(temporaryAsset.option.optionId, asset.singleVersionNumber);
  }
  if (temporaryAsset.option.thumbnails) {
    asset.thumbnails = Object.values(temporaryAsset.option.thumbnails);
  }
  return asset;
}

function createVersionAsset(temporaryAsset: ShareLinkAssetVM, baseAsset: Partial<ShareLinkAsset>): ShareLinkAsset {
  if (!temporaryAsset.option || !temporaryAsset.version) {
    throw new Error('Option and version data are required for VERSION type');
  }

  const versionId: string = getVersionId(temporaryAsset.option.optionId, temporaryAsset.version.versionNumber);

  return {
    ...baseAsset,
    itemId: versionId,
    name: `${temporaryAsset.option.title} - ${temporaryAsset.version.name}`,
    versionId,
  } as ShareLinkAsset;
}

function createFileAsset(temporaryAsset: ShareLinkAssetVM, baseAsset: Partial<ShareLinkAsset>): ShareLinkAsset {
  if (!temporaryAsset.file) {
    throw new Error('File data is required for FILE type');
  }
  return {
    ...baseAsset,
    itemId: temporaryAsset.file.id,
    fileId: temporaryAsset.file.id,
    name: temporaryAsset.file.displayName,
    previewImages: [temporaryAsset.file.signedURL],
    transcodeMetadata: temporaryAsset.file.transcodeMetadata,
    malwareScanningStatus: temporaryAsset.file.malwareScanningStatus,
    signedURL: temporaryAsset.file.signedURL,
    contentType: temporaryAsset.file.contentType,
  } as ShareLinkAsset;
}

export function getShareLinkAssetFromVM(temporaryAsset: ShareLinkAssetVM): ShareLinkAsset {
  if (!temporaryAsset.type) {
    throw new Error('Asset type is required');
  }

  const baseAsset: Partial<ShareLinkAsset> = createBaseAsset(temporaryAsset);

  switch (temporaryAsset.type) {
    case ShareLinkAssetEntityTypes.SUBSECTION:
      return createSubsectionAsset(temporaryAsset, baseAsset);

    case ShareLinkAssetEntityTypes.FOLDER:
      return createFolderAsset(temporaryAsset, baseAsset);

    case ShareLinkAssetEntityTypes.SUBFOLDER:
      return createSubfolderAsset(temporaryAsset, baseAsset);

    case ShareLinkAssetEntityTypes.VERSION:
      return createVersionAsset(temporaryAsset, baseAsset);

    case ShareLinkAssetEntityTypes.FILE:
      return createFileAsset(temporaryAsset, baseAsset);

    default:
      throw new Error(`Unsupported asset type: ${temporaryAsset.type}`);
  }
}

export function copyShareLinkUrl(params: {
  shareLink: ShareLinkSimple;
  shareLinkService: ShareLinkTreeService;
  overlayService: OverlayService;
  translatePipe: TranslatePipe;
  subsectionService: SubsectionService;
}): void {
  params.shareLinkService.copyLinkById(params.shareLink.id);
  params.overlayService.showSuccess(params.translatePipe.transform('copiedToClipboard'));
}

export function openShareLinkSettings(params: {
  shareLink: ShareLinkSimple;
  subsectionService: SubsectionService;
  projectId: string;
  sectionId: string;
  subsectionId: string;
}): void {
  params.subsectionService.navigateSubsection({
    projectId: params.projectId,
    sectionId: params.sectionId,
    subsectionId: params.subsectionId,
    navigationType: 'share-link',
    shareLinkId: params.shareLink.id,
  });
}

export function shareAsset(params: {
  asset: ShareLinkAssetVM;
  shareLinkService: ShareLinkTreeService;
  overlayService: OverlayService;
  translatePipe: TranslatePipe;
  subsectionService: SubsectionService;
}): Observable<ShareLink | null> {
  return params.shareLinkService.createNewShareFromAssetVM(params.asset).pipe(
    tap((shareLink: ShareLink) => {
      EmitterService.get(SHARE_LINK_EVENT.CREATE).emit({ isNew: true, data: shareLink });
    }),
    catchError((err) => {
      params.overlayService.showError(params.translatePipe.transform('overlaySaveError'));
      return of(null);
    })
  );
}

export function newEmptyShareLink(params: {
  shareTreeService: ShareLinkTreeService;
  overlayService: OverlayService;
  translatePipe: TranslatePipe;
  projectId: string;
  sectionId: string;
  subsectionId: string;
}): Observable<ShareLink | null> {
  const { shareTreeService, overlayService, translatePipe, projectId, sectionId, subsectionId } = params;

  return shareTreeService.createEmptyShareLink({ projectId, sectionId, subsectionId }).pipe(
    tap((shareLink: ShareLink) => {
      EmitterService.get(SHARE_LINK_EVENT.CREATE).emit({ isNew: true, data: shareLink });
    }),
    catchError((err) => {
      overlayService.showError(translatePipe.transform('overlaySaveError'));
      return of(null);
    })
  );
}

export function redirectToShareAssetUrl(args: {
  asset: ShareLinkAsset;
  subsectionService: SubsectionService;
  projectId: string;
  sectionId: string;
  subsectionId: string;
  shareLinkId: string;
}): void {
  const { asset, subsectionService, projectId, sectionId, subsectionId, shareLinkId } = args;
  const params: SubsectionNavigationParams = {
    projectId,
    sectionId,
    subsectionId,
    navigationType: 'share-link',
    shareLinkId,
    shareAssetId: asset.itemId,
  };

  switch (asset.entityType) {
    case ShareLinkAssetEntityTypes.FOLDER:
      params.topicId = asset.topicId;
      break;

    case ShareLinkAssetEntityTypes.SUBFOLDER:
      params.topicId = asset.topicId;
      params.optionId = asset.optionId;
      break;

    case ShareLinkAssetEntityTypes.VERSION:
      params.topicId = asset.topicId;
      params.optionId = asset.optionId;
      params.versionNumber = asset.version;
      break;

    case ShareLinkAssetEntityTypes.FILE:
      params.topicId = asset.topicId;
      params.optionId = asset.optionId;
      params.versionNumber = asset.version;
      params.fileId = asset.fileId;
      break;
  }

  subsectionService.navigateSubsection(params);
}
